/**
 * ProcRowModals — Item History, Supplier History, Split Barcode/IMEI modals
 * Triggered from ProcItemGrid ⋮ context menu
 */
import React, { useState } from 'react';

// ─── Shared overlay wrapper ───────────────────────────────────────────────
function ModalWrap({ title, subtitle, onClose, children, width = 760 }) {
  return (
    <>
      <div className="pi2-overlay-bg" onClick={onClose} />
      <div className="pi2-modal-wrap">
        <div className="pi2-modal" style={{ width, maxWidth: '95vw' }}>
          <div className="pi2-modal-head">
            <div className="t"><small>{subtitle}</small>{title}</div>
            <button className="x" onClick={onClose}>✕</button>
          </div>
          {children}
        </div>
      </div>
    </>
  );
}

// ─── Item History Modal ────────────────────────────────────────────────────
export function ItemHistoryModal({ row, onClose }) {
  const itemCode = row?.code || '—';
  const itemName = row?.name || '—';

  // Demo data — will be replaced with API call
  const history = [
    { date:'26-May-2026', docNo:'GRN101N0006', docType:'GRN',       supplier:'MYACCOUNTS',    qty:5, rate:520.00, mrp:999.00 },
    { date:'15-Apr-2026', docNo:'GRN101N0005', docType:'GRN',       supplier:'REDDY TRADERS', qty:3, rate:500.00, mrp:999.00 },
    { date:'10-Mar-2026', docNo:'PI101N0003',  docType:'PI',        supplier:'MYACCOUNTS',    qty:10,rate:480.00, mrp:950.00 },
    { date:'05-Feb-2026', docNo:'GRN101N0002', docType:'GRN',       supplier:'ABC TRADERS',   qty:2, rate:510.00, mrp:999.00 },
    { date:'20-Jan-2026', docNo:'PI101N0001',  docType:'PI Return', supplier:'MYACCOUNTS',    qty:1, rate:490.00, mrp:950.00 },
  ];

  const avg = history.length ? (history.reduce((s, r) => s + r.rate, 0) / history.length).toFixed(2) : '0.00';

  return (
    <ModalWrap title="Item Purchase History" subtitle={`${itemCode} · ${itemName}`} onClose={onClose} width={820}>
      <div style={{ padding: '8px 12px', background: '#f0f4f8', display: 'flex', gap: 24, fontSize: 12, borderBottom: '1px solid var(--pi2-border)' }}>
        <span><b>Item Code:</b> {itemCode}</span>
        <span><b>Item Name:</b> {itemName}</span>
        <span><b>Avg Purchase Rate:</b> ₹{avg}</span>
        <span><b>Total Txns:</b> {history.length}</span>
      </div>
      <div style={{ overflow: 'auto', maxHeight: 380 }}>
        <table className="pi2-table" style={{ width: '100%' }} data-testid="item-history-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Doc No</th>
              <th>Type</th>
              <th>Supplier</th>
              <th className="pi2-num">Qty</th>
              <th className="pi2-num">Rate</th>
              <th className="pi2-num">MRP</th>
            </tr>
          </thead>
          <tbody>
            {history.map((r, i) => (
              <tr key={i}>
                <td>{r.date}</td>
                <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{r.docNo}</td>
                <td><span style={{ background: r.docType.includes('Return') ? '#fce7f3' : '#dbeafe', color: r.docType.includes('Return') ? '#be185d' : '#1d4ed8', padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 700 }}>{r.docType}</span></td>
                <td>{r.supplier}</td>
                <td className="pi2-num pi2-mono">{r.qty}</td>
                <td className="pi2-num pi2-mono">₹{r.rate.toFixed(2)}</td>
                <td className="pi2-num pi2-mono">₹{r.mrp.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pi2-modal-foot">
        <span style={{ fontSize: 11, color: 'var(--pi2-ink-soft)' }}>Showing last {history.length} transactions</span>
        <button className="pi2-btn" onClick={onClose}>Close</button>
      </div>
    </ModalWrap>
  );
}

// ─── Supplier History Modal ────────────────────────────────────────────────
export function SupplierHistoryModal({ row, supplierName, onClose }) {
  const supplier = supplierName || row?.vendor || 'Supplier';

  const history = [
    { date:'26-May-2026', docNo:'GRN101N0006', docType:'GRN',    items:5, netAmt:2600.00, status:'Received' },
    { date:'15-Apr-2026', docNo:'PI101N0005',  docType:'PI',     items:3, netAmt:1500.00, status:'Invoiced' },
    { date:'10-Mar-2026', docNo:'GRN101N0004', docType:'GRN',    items:8, netAmt:4800.00, status:'Received' },
    { date:'01-Feb-2026', docNo:'GRN101N0002', docType:'GRN',    items:2, netAmt:1200.00, status:'Received' },
    { date:'15-Jan-2026', docNo:'PI101N0001',  docType:'PI Ret', items:1, netAmt: 600.00, status:'Returned' },
  ];

  const totalAmt = history.reduce((s, r) => s + r.netAmt, 0);

  return (
    <ModalWrap title="Supplier Transaction History" subtitle={supplier} onClose={onClose} width={780}>
      <div style={{ padding: '8px 12px', background: '#f0f4f8', display: 'flex', gap: 24, fontSize: 12, borderBottom: '1px solid var(--pi2-border)' }}>
        <span><b>Supplier:</b> {supplier}</span>
        <span><b>Total Transactions:</b> {history.length}</span>
        <span><b>Total Value:</b> ₹{totalAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
      </div>
      <div style={{ overflow: 'auto', maxHeight: 380 }}>
        <table className="pi2-table" style={{ width: '100%' }} data-testid="supplier-history-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Doc No</th>
              <th>Type</th>
              <th className="pi2-num">Items</th>
              <th className="pi2-num">Net Amount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {history.map((r, i) => (
              <tr key={i}>
                <td>{r.date}</td>
                <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{r.docNo}</td>
                <td><span style={{ background: r.docType.includes('Ret') ? '#fce7f3' : '#dcfce7', color: r.docType.includes('Ret') ? '#be185d' : '#15803d', padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 700 }}>{r.docType}</span></td>
                <td className="pi2-num pi2-mono">{r.items}</td>
                <td className="pi2-num pi2-mono">₹{r.netAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                <td><span style={{ fontSize: 11, color: r.status === 'Returned' ? '#c0392b' : '#16a34a' }}>{r.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pi2-modal-foot">
        <span style={{ fontSize: 11, color: 'var(--pi2-ink-soft)' }}>Showing last {history.length} transactions</span>
        <button className="pi2-btn" onClick={onClose}>Close</button>
      </div>
    </ModalWrap>
  );
}

// ─── Split Barcode / IMEI Details Modal ────────────────────────────────────
export function SplitBarcodeModal({ row, onSave, onClose }) {
  const qty = Math.max(1, parseInt(row?.qty) || 1);
  const baseCode = row?.code || '';

  const [serials, setSerials] = useState(() =>
    Array.from({ length: qty }, (_, i) => ({
      sl: i + 1,
      barcode: baseCode ? `${baseCode}-${String(i + 1).padStart(3, '0')}` : '',
      imei: '',
      serial: '',
      mfgDate: '',
      expiryDate: '',
    }))
  );

  function setSField(idx, k, v) {
    setSerials(s => s.map((r, i) => i === idx ? { ...r, [k]: v } : r));
  }

  function autoGenerate() {
    setSerials(s => s.map((r, i) => ({
      ...r,
      barcode: `${baseCode}-${String(i + 1).padStart(3, '0')}`,
      imei: `IMEI${Date.now()}${String(i).padStart(3, '0')}`,
    })));
  }

  function handleSave() {
    const filled = serials.filter(r => r.barcode || r.imei || r.serial);
    onSave && onSave(filled);
    onClose();
  }

  return (
    <ModalWrap title="Split Barcode / IMEI Details" subtitle={`${row?.code || ''} · ${row?.name || ''}`} onClose={onClose} width={900}>
      <div className="pi2-modal-toast">
        ℹ️ Enter individual barcode, IMEI, or serial number for each unit of Qty {qty}. Click "Auto Generate" to fill barcodes automatically.
      </div>
      <div style={{ padding: '6px 12px', display: 'flex', gap: 8, borderBottom: '1px solid var(--pi2-border)', alignItems: 'center' }}>
        <span style={{ fontSize: 12 }}><b>Item:</b> {row?.name || '—'} &nbsp;|&nbsp; <b>Qty:</b> {qty} units</span>
        <button className="pi2-btn primary" style={{ marginLeft: 'auto' }} type="button" onClick={autoGenerate}>
          ⚙ Auto Generate Barcodes
        </button>
      </div>
      <div style={{ overflow: 'auto', maxHeight: 400 }}>
        <table className="pi2-table" style={{ width: '100%' }} data-testid="split-barcode-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>Sl.No</th>
              <th>Barcode / OEM Code</th>
              <th>IMEI No</th>
              <th>Serial No</th>
              <th>Mfg Date</th>
              <th>Expiry Date</th>
            </tr>
          </thead>
          <tbody>
            {serials.map((r, i) => (
              <tr key={i}>
                <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--pi2-ink-soft)' }}>{r.sl}</td>
                {['barcode', 'imei', 'serial', 'mfgDate', 'expiryDate'].map(k => (
                  <td key={k}>
                    <input
                      className="pi2-cell-edit"
                      value={r[k]}
                      placeholder={k === 'mfgDate' || k === 'expiryDate' ? 'DD-MM-YYYY' : ''}
                      onChange={e => setSField(i, k, e.target.value)}
                      style={{ width: '100%', height: 28 }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pi2-modal-foot">
        <span style={{ fontSize: 11, color: 'var(--pi2-ink-soft)' }}>
          {serials.filter(r => r.barcode || r.imei).length} of {qty} units filled
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="pi2-btn" onClick={onClose}>Cancel</button>
          <button className="pi2-btn primary" onClick={handleSave} data-testid="split-barcode-save-btn">
            Save IMEI / Barcode Details
          </button>
        </div>
      </div>
    </ModalWrap>
  );
}
